import {
  Box,
  FormControl,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AzureOnboardingStatusStatusEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import CopyableCode from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupOwnCloudState } from './SetupOwnCloudFlow';
import { BulletItem } from './bulletItem';
import { FormTextField } from './formTextField';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';

export const ConnectSourceAccount = (props: StepProps<SetupOwnCloudState>) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const dal = useDAL();
  const [error, setError] = useState<Error | null>(null);

  const code = `\
module "azure_source" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/source.zip"

  eon_backup_app_id      = "${props.wizardState.eonBackupApplicationId ?? ''}"
  customer_backup_app_id = "${props.wizardState.customerBackupAppId ?? ''}"
  subscription_id        = "${props.wizardState.sourceSubscriptionId ?? ''}"
  management_group_id    = "${props.wizardState.sourceManagementGroupId ?? ''}"
}`;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const response = await dal.onboarding.sourceAccount.deploy({
        tenantId: props.wizardState.tenantId,
        sourceSubscriptionId: props.wizardState.sourceSubscriptionId!,
        sourceManagementGroupId: props.wizardState.sourceManagementGroupId!,
      });

      // Check if the response has a status field and it's not succeeded
      if (
        response &&
        response.status !== AzureOnboardingStatusStatusEnum.Succeeded
      ) {
        console.log(response);
        setError(new Error('Failed to deploy source account'));
      } else {
        setIsDeploying(false);
        await props.finish();
      }
    } catch (error) {
      setError(new Error(error as string));
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={3}
        stepperLabels={props.stepperLabels}
        canGoNext={() =>
          !isDeploying && props.wizardState.connectType === 'subscription'
            ? !!props.wizardState.sourceSubscriptionId
            : !!props.wizardState.sourceManagementGroupId
        }
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
          <BulletItem
            title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
            leftContent={
              <Stack gap='24px'>
                <FormControl>
                  <FormLabel>
                    <Typography variant='subtitle1'>
                      What do you want to connect?
                    </Typography>
                  </FormLabel>
                  <ToggleButtonGroup
                    sx={{ width: 'max-content' }}
                    exclusive
                    value={props.wizardState.connectType}
                    onChange={(_, value) =>
                      props.setWizardState({
                        ...props.wizardState,
                        connectType: value,
                      })
                    }
                  >
                    <ToggleButton value='subscription'>
                      Subscription
                    </ToggleButton>
                    <ToggleButton value='managementGroup'>
                      Management group
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
                {props.wizardState.connectType === 'subscription' && (
                  <FormTextField
                    title='Subscription ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.sourceSubscriptionId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceSubscriptionId: event.target.value,
                      })
                    }
                  />
                )}
                {props.wizardState.connectType === 'managementGroup' && (
                  <FormTextField
                    title='Management group ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.sourceManagementGroupId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        sourceManagementGroupId: event.target.value,
                      })
                    }
                  />
                )}
              </Stack>
            }
            rightContent={<CopyableCode title='main.tf' code={code} />}
          />
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
